import React, { useEffect, useState } from 'react';
import {
  formatCurrency,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  handleApiError
} from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { DateRangeType, Dropdown } from '@cokitchen/cokitchen-components';
import { ReactComponent as ToolTipIcon } from 'assets/icons/tooltip.svg';
import { MonthlyBreakdownStatType } from 'models/statements.model';

export type Props = {
  dateRange: DateRangeType;
};

const initialBreakDownData: MonthlyBreakdownStatType = {
  balance: '',
  budget: '',
  gift: '',
  gift_percentage_increase: null,
  previous_gift: '',
  previous_spent: '',
  spent: '',
  spent_percentage_increase: null
};

const InsightDashboard = (props: Props) => {
  const { dateRange } = props;
  const [breakDownData, setBreakDownData] =
    useState<MonthlyBreakdownStatType>(initialBreakDownData);
  const [budget, setBudget] = useState('');

  const start_date = dateRange.date.startDate.toISOString();
  const end_date = dateRange.date.endDate.toISOString();

  const { setHideSidebar, setBackLinkTitle, setIsBackLink, activeCompany } = useDashboardContext();

  const getMonthlyBreakdown = async () => {
    if (!activeCompany.id) return;
    try {
      const res = await apiService.get('admin/monthly-breakdown-stat', {
        params: {
          start_date: start_date ?? getFirstDayOfMonth(),
          end_date: end_date ?? getLastDayOfMonth(),
          company_id: activeCompany.id
        }
      });
      setBreakDownData(res.data.data);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadBudget = async () => {
    try {
      const params = { start_date, end_date };
      const res = await apiService.get(`admin/budget/${activeCompany.id}`, { params });
      setBudget(res.data.data.total_budget);
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    getMonthlyBreakdown();
    loadBudget();
  }, [activeCompany, dateRange]);

  useEffect(() => {
    setHideSidebar(false);
    setIsBackLink(true);
    setBackLinkTitle('Return to Dashboard');
    return () => {
      setHideSidebar(false);
      setIsBackLink(false);
      setBackLinkTitle('');
    };
  }, []);

  return (
    <section className='rounded bg-white border border-[#EAEBEB] md:grid md:grid-cols-2 xl:grid-cols-4 items-center justify-between'>
      <div className='max-md:border-b md:border-r border-[#EAEBEB] flex place-content-center place-items-center'>
        <div className='py-7'>
          <p className='font-euclid text-xs lg:text-sm font-normal text-gray-4 pb-3'>
            Total Budget
          </p>
          <h3 className='text-[24px] lg:text-[36px] leading-[36px] py-3'>
            <small className='text-[10px] mr-1 mt-1 text-[#2E3031] font-normal self-start'>
              NGN
            </small>
            <span className='font-bold'>
              {formatCurrency(budget, { style: 'decimal' }, true)}
              {budget && parseFloat(budget) >= 10000000 && (
                <div className='inline-block pl-1'>
                  <Dropdown
                    placement='right'
                    className='w-[200px]'
                    tooltip
                    content={<ToolTipIcon />}>
                    <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                      The figure displayed was rounded up. The actual wallet balance is{' '}
                      {formatCurrency(budget)}
                    </div>
                  </Dropdown>
                </div>
              )}
            </span>
          </h3>
        </div>
      </div>
      <div className='max-md:border-b xl:border-r border-[#EAEBEB] flex place-content-center place-items-center'>
        <div className='py-7'>
          <p className='text-xs lg:text-sm font-normal font-euclid  text-gray-4 pb-3'>Balance</p>

          <h3 className='text-[24px] lg:text-[36px] leading-[36px] py-3'>
            <small className='text-[10px] font-normal mr-1 mt-1 text-[#2E3031] self-start'>
              NGN
            </small>
            <span className='font-bold '>
              {formatCurrency(
                breakDownData.balance,
                {
                  style: 'decimal'
                },
                true
              )}
              {breakDownData?.balance && parseFloat(breakDownData?.balance) >= 10000000 && (
                <div className='inline-block pl-1'>
                  <Dropdown
                    placement='right'
                    className='w-[200px]'
                    tooltip
                    content={<ToolTipIcon />}>
                    <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                      The figure displayed was rounded up. The actual wallet balance is{' '}
                      {formatCurrency(breakDownData?.balance)}
                    </div>
                  </Dropdown>
                </div>
              )}
            </span>
          </h3>
        </div>
      </div>
      <div className='max-md:border-b md:border-r border-[#EAEBEB] flex place-content-center place-items-center'>
        <div className='py-7'>
          <div className='flex gap-2 items-center pb-3'>
            <p className=' text-xs lg:text-sm font-normal font-euclid  text-gray-4'>Spent</p>
          </div>
          <h3 className='text-[24px] lg:text-[36px] leading-[36px] py-3'>
            <small className='text-[10px] mr-1 mt-1 text-[#2E3031] font-normal self-start'>
              NGN
            </small>
            <span className='font-bold'>
              {formatCurrency(
                breakDownData?.spent,
                {
                  style: 'decimal'
                },
                true
              )}
              {breakDownData?.spent && parseFloat(breakDownData?.spent) >= 10000000 && (
                <div className='inline-block pl-1'>
                  <Dropdown
                    placement='right'
                    className='w-[200px]'
                    tooltip
                    content={<ToolTipIcon />}>
                    <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                      The figure displayed was rounded up. The amount spent is{' '}
                      {formatCurrency(breakDownData?.spent)}
                    </div>
                  </Dropdown>
                </div>
              )}
            </span>
          </h3>
          <span className='text-green-5 font-medium text-xs'>
            {/* {percentage_deviation?.revenue ?? '-'}% <span className='lowercase'>Today</span> */}
          </span>
        </div>
      </div>

      <div className='flex place-content-center place-items-center'>
        <div className='py-7'>
          <div className='flex gap-2 items-center pb-3'>
            <p className=' text-xs lg:text-sm font-normal font-euclid  text-gray-4'>Gift</p>
            {/* <p className='bg-[#3FB827]  text-white rounded-full py-[2px] px-2   text-xs'>
              {Number(breakDownData?.gift_percentage_increase) ?? '-'}%
            </p> */}
          </div>
          <h3 className='text-[24px] lg:text-[36px] leading-[36px] py-3'>
            <small className='text-[10px] mr-1 mt-1 text-[#2E3031] font-normal self-start'>
              NGN
            </small>
            <span className='font-bold'>
              {formatCurrency(
                breakDownData?.gift,
                {
                  style: 'decimal'
                },
                true
              )}
              {breakDownData?.gift && parseFloat(breakDownData?.gift) >= 10000000 && (
                <div className='inline-block pl-1'>
                  <Dropdown
                    placement='right'
                    className='w-[200px]'
                    tooltip
                    content={<ToolTipIcon />}>
                    <div className='w-[200px] text-gray-5 p-2 bg-white border border-gray-1 rounded'>
                      The figure displayed was rounded up. The amount gifted is{' '}
                      {formatCurrency(breakDownData?.gift)}
                    </div>
                  </Dropdown>
                </div>
              )}
            </span>
          </h3>
          <span className='text-green-5 font-medium text-xs'>
            {/* {percentage_deviation?.revenue ?? '-'}% <span className='lowercase'>Today</span> */}
          </span>
        </div>
      </div>
    </section>
  );
};

export default InsightDashboard;
